/**
 * Capacitor clipboard bridge.
 *
 * Uses the @capacitor/clipboard plugin, whose JS interface must be bundled into
 * the web app (this is a remote-webview shell, so plugin JS ships with the site).
 * All calls are guarded by isCapacitorEnvironment() so they never touch the native
 * bridge on plain web. mobiloud/clipboard.js delegates here before falling back to
 * the MobiLoud nativeFunctions path and finally the Web Clipboard API.
 */

import { Clipboard } from "@capacitor/clipboard";
import { isCapacitorEnvironment } from "./detection";

/**
 * @returns {boolean} whether the Capacitor clipboard bridge is usable
 */
export function hasCapacitorClipboard() {
  return isCapacitorEnvironment();
}

/**
 * Write text to the native clipboard.
 * @param {string} text
 * @returns {Promise<boolean>} true if written via Capacitor, false if
 *   unavailable or the write failed (e.g. permission denied / missing plugin) —
 *   never rejects, so callers can safely fall back to other strategies.
 */
export async function capacitorWriteClipboard(text) {
  if (!hasCapacitorClipboard()) {
    return false;
  }
  try {
    await Clipboard.write({ string: text });
    return true;
  } catch {
    return false;
  }
}

/**
 * Read text from the native clipboard.
 * @returns {Promise<string|null>} the text, or null if unavailable/failed
 */
export async function capacitorReadClipboard() {
  if (!hasCapacitorClipboard()) {
    return null;
  }
  try {
    const { value } = await Clipboard.read();
    return typeof value === "string" ? value : null;
  } catch {
    return null;
  }
}
